import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { fetchCoordinators, createCoordinator, updateCoordinator, linkCoordinatorUser, disableCoordinator, enableCoordinator, deleteCoordinator, linkStudentEmail } from "./commissionService";
import api from "../../services/admin_api_global";
import PhoneInput from "../../../common/components/PhoneInput";
import axios from "axios";
import "../payments/paymentDashboard.css";

const COORD_TYPES   = ["STUDENT", "SPEAKER", "EXTERNAL"];
const PAYMENT_MODES = ["BANK", "UPI", "CASH", "OTHER"];

const TYPE_BADGE = {
  STUDENT:  "pd-badge-blue",
  SPEAKER:  "pd-badge-purple",
  EXTERNAL: "pd-badge-amber",
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const emptyForm = () => ({
  coordinatorType: "STUDENT",
  userId: "", userDisplayName: "",
  speakerId: "", speakerDisplayName: "",
  name: "", phone: "", countryCode: "+91", email: "",
  defaultCommissionPercent: "",
  paymentMode: "",
  bankName: "", accountNumber: "", ifscCode: "", accountHolderName: "",
  upiId: "", paymentNotes: "", notes: "",
});

// ── User Search Input ─────────────────────────────────────────
const UserSearchInput = ({ displayName, onChange }) => {
  const [query,   setQuery]   = useState(displayName || "");
  const [results, setResults] = useState([]);
  const [open,    setOpen]    = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const handleChange = (e) => {
    const q = e.target.value;
    setQuery(q);
    setOpen(false);
    if (!q.trim()) { onChange(null); setResults([]); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setLoading(true);
      api.get("/admin/users", { params: { page: 0, size: 8, search: q } })
        .then((res) => {
          const list = res.data?.data || res.data?.content || (Array.isArray(res.data) ? res.data : []);
          setResults(list);
          setOpen(true);
        })
        .catch(() => { setResults([]); setOpen(true); })
        .finally(() => setLoading(false));
    }, 400);
  };

  const select = (u) => {
    setQuery(u.name || `User #${u.id}`);
    setOpen(false);
    setResults([]);
    onChange(u);
  };

  return (
    <div style={{ position: "relative" }}>
      <input value={query} onChange={handleChange} placeholder="Search by name or email…"
        style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: "0.875rem", outline: "none", boxSizing: "border-box" }} />
      {loading && <div style={{ position: "absolute", right: 10, top: 9, fontSize: "0.75rem", color: "#94a3b8" }}>…</div>}
      {open && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 200, maxHeight: 220, overflowY: "auto" }}>
          {results.length === 0
            ? <div style={{ padding: "10px 12px", fontSize: "0.82rem", color: "#94a3b8" }}>No users found.</div>
            : results.map((u) => (
              <div key={u.id} onMouseDown={() => select(u)}
                style={{ padding: "8px 12px", cursor: "pointer", fontSize: "0.82rem", borderBottom: "1px solid #f1f5f9" }}
                onMouseEnter={(e) => e.currentTarget.style.background = "#f8fafc"}
                onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}>
                <span style={{ fontWeight: 600 }}>{u.name}</span>
                {u.email && <span style={{ color: "#6b7280", marginLeft: 8 }}>{u.email}</span>}
                <span style={{ color: "#94a3b8", marginLeft: 8, fontSize: "0.75rem" }}>#{u.id}</span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

// ── Speaker Search Input ──────────────────────────────────────
const SpeakerSearchInput = ({ displayName, onChange }) => {
  const [query,   setQuery]   = useState(displayName || "");
  const [all,     setAll]     = useState([]);
  const [results, setResults] = useState([]);
  const [open,    setOpen]    = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/speakers")
      .then((res) => setAll(res.data?.content || (Array.isArray(res.data) ? res.data : [])))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (e) => {
    const q = e.target.value;
    setQuery(q);
    if (!q.trim()) { onChange(null); setResults([]); setOpen(false); return; }
    const filtered = all.filter((s) => (s.name || "").toLowerCase().includes(q.toLowerCase()));
    setResults(filtered);
    setOpen(true);
  };

  const select = (s) => {
    setQuery(s.name);
    setOpen(false);
    setResults([]);
    onChange(s);
  };

  return (
    <div style={{ position: "relative" }}>
      <input value={query} onChange={handleChange}
        placeholder={loading ? "Loading speakers…" : "Search speaker by name…"}
        disabled={loading}
        style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: "0.875rem", outline: "none", boxSizing: "border-box" }} />
      {open && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 200, maxHeight: 220, overflowY: "auto" }}>
          {results.length === 0
            ? <div style={{ padding: "10px 12px", fontSize: "0.82rem", color: "#94a3b8" }}>No speakers found.</div>
            : results.map((s) => (
              <div key={s.id} onMouseDown={() => select(s)}
                style={{ padding: "8px 12px", cursor: "pointer", fontSize: "0.82rem", borderBottom: "1px solid #f1f5f9" }}
                onMouseEnter={(e) => e.currentTarget.style.background = "#f8fafc"}
                onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}>
                <span style={{ fontWeight: 600 }}>{s.name}</span>
                <span style={{ color: "#94a3b8", marginLeft: 8, fontSize: "0.75rem" }}>#{s.id}</span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

// ── Toast ─────────────────────────────────────────────────────
const Toast = ({ msg, type, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  return <div className={`pd-toast ${type === "error" ? "pd-toast-error" : "pd-toast-success"}`}>{msg}</div>;
};

// ── Confirm Dialog ────────────────────────────────────────────
const ConfirmDialog = ({ message, onConfirm, onCancel, danger }) => (
  <div className="pd-overlay">
    <div className="pd-modal pd-modal-sm">
      <p className="pd-confirm-msg">{message}</p>
      <div className="pd-modal-actions">
        <button className={`pd-btn ${danger ? "pd-btn-danger" : "pd-btn-primary"}`} onClick={onConfirm}>Confirm</button>
        <button className="pd-btn pd-btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  </div>
);

// ── Link User Modal ───────────────────────────────────────────
const LinkUserModal = ({ coordinator, onClose, onSuccess }) => {
  const [userId,    setUserId]    = useState(coordinator.userId    || "");
  const [speakerId, setSpeakerId] = useState(coordinator.speakerId || "");
  const [saving,    setSaving]    = useState(false);
  const [err,       setErr]       = useState("");

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const body = {};
      if (userId)    body.userId    = Number(userId);
      if (speakerId) body.speakerId = Number(speakerId);
      await linkCoordinatorUser(coordinator.id, body);
      onSuccess("User linked successfully.");
    } catch (e) { setErr(e?.response?.data?.message || "Failed to link."); }
    finally { setSaving(false); }
  };

  return (
    <div className="pd-overlay">
      <div className="pd-modal pd-modal-sm">
        <h3 className="pd-modal-title">Link User — {coordinator.name}</h3>
        <div className="pd-form-group">
          <label>User ID</label>
          <input type="number" value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="Registered user ID" />
        </div>
        <div className="pd-form-group">
          <label>Speaker ID</label>
          <input type="number" value={speakerId} onChange={(e) => setSpeakerId(e.target.value)} placeholder="Speaker ID" />
        </div>
        {err && <div className="pd-error">{err}</div>}
        <div className="pd-modal-actions">
          <button className="pd-btn pd-btn-primary" disabled={saving} onClick={save}>{saving ? "Saving…" : "Link"}</button>
          <button className="pd-btn pd-btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ── Add / Edit Modal ──────────────────────────────────────────
const CoordinatorModal = ({ initial, onClose, onSuccess }) => {
  const [form,         setForm]         = useState(initial ? {
    coordinatorType:          initial.coordinatorType          || "STUDENT",
    userId:                   initial.userId                   || "",
    userDisplayName:          initial.userId ? `User #${initial.userId}` : "",
    speakerId:                initial.speakerId                || "",
    speakerDisplayName:       initial.speakerId ? `Speaker #${initial.speakerId}` : "",
    name:                     initial.name                     || "",
    phone:                    initial.phone                    || "",
    countryCode:              initial.countryCode              || "+91",
    email:                    initial.email                    || "",
    defaultCommissionPercent: initial.defaultCommissionPercent != null ? initial.defaultCommissionPercent : "",
    paymentMode:              initial.paymentMode              || "",
    bankName:                 initial.bankName                 || "",
    accountNumber:            initial.accountNumber            || "",
    ifscCode:                 initial.ifscCode                 || "",
    accountHolderName:        initial.accountHolderName        || "",
    upiId:                    initial.upiId                    || "",
    paymentNotes:             initial.paymentNotes             || "",
    notes:                    initial.notes                    || "",
  } : emptyForm());

  const [countryCodes, setCountryCodes] = useState([]);
  const [saving,       setSaving]       = useState(false);
  const [err,          setErr]          = useState("");

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BASE_URL}/public/country-codes`)
      .then((res) => setCountryCodes(res.data?.data || []))
      .catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // When type changes, reset linked fields and auto-filled contact info
  const handleTypeChange = (type) => {
    setForm((f) => ({
      ...f,
      coordinatorType: type,
      userId: "", userDisplayName: "",
      speakerId: "", speakerDisplayName: "",
      name: "", phone: "", countryCode: "+91", email: "",
    }));
  };

  // STUDENT selected → auto-fill name, email, phone from user object
  const handleUserSelect = (u) => {
    if (!u) { setForm((f) => ({ ...f, userId: "", userDisplayName: "", name: "", email: "", phone: "", countryCode: "+91" })); return; }
    setForm((f) => ({
      ...f,
      userId:          u.id,
      userDisplayName: u.name || `User #${u.id}`,
      name:            u.name        || f.name,
      email:           u.email       || f.email,
      phone:           u.mobileNumber || f.phone,
      countryCode:     u.countryCode  || f.countryCode,
    }));
  };

  // SPEAKER selected → auto-fill name only (speakers have no phone/email)
  const handleSpeakerSelect = (s) => {
    if (!s) { setForm((f) => ({ ...f, speakerId: "", speakerDisplayName: "", name: "" })); return; }
    setForm((f) => ({
      ...f,
      speakerId:          s.id,
      speakerDisplayName: s.name,
      name:               s.name || f.name,
    }));
  };

  const isLinked = form.coordinatorType !== "EXTERNAL";
  // For STUDENT/SPEAKER: name/email/phone are read-only (auto-filled)
  // For SPEAKER: email/phone not shown (speaker has none)
  const isExternal = form.coordinatorType === "EXTERNAL";
  const isStudent  = form.coordinatorType === "STUDENT";

  const validate = () => {
    if (!form.name.trim())     return "Name is required.";
    if (isStudent  && !form.userId)    return "Please select a user.";
    if (!isExternal && !isStudent && !form.speakerId) return "Please select a speaker.";
    const pct = parseFloat(form.defaultCommissionPercent);
    if (form.defaultCommissionPercent === "" || isNaN(pct) || pct < 0 || pct > 100)
      return "Commission % must be between 0 and 100.";
    return null;
  };

  const save = async () => {
    const vErr = validate();
    if (vErr) { setErr(vErr); return; }
    setSaving(true); setErr("");
    try {
      const body = {
        name:                     form.name.trim(),
        coordinatorType:          form.coordinatorType,
        defaultCommissionPercent: parseFloat(form.defaultCommissionPercent),
        ...(form.phone             && { phone: form.phone.trim(), countryCode: form.countryCode }),
        ...(form.email             && { email: form.email.trim() }),
        ...(form.userId            && { userId: Number(form.userId) }),
        ...(form.speakerId         && { speakerId: Number(form.speakerId) }),
        ...(form.paymentMode       && { paymentMode: form.paymentMode }),
        ...(form.bankName          && { bankName: form.bankName.trim() }),
        ...(form.accountNumber     && { accountNumber: form.accountNumber.trim() }),
        ...(form.ifscCode          && { ifscCode: form.ifscCode.trim() }),
        ...(form.accountHolderName && { accountHolderName: form.accountHolderName.trim() }),
        ...(form.upiId             && { upiId: form.upiId.trim() }),
        ...(form.paymentNotes      && { paymentNotes: form.paymentNotes.trim() }),
        ...(form.notes             && { notes: form.notes.trim() }),
      };
      if (initial?.id) await updateCoordinator(initial.id, body);
      else             await createCoordinator(body);
      onSuccess(initial?.id ? "Coordinator updated." : "Coordinator added.");
    } catch (e) { setErr(e?.response?.data?.message || "Failed to save."); }
    finally { setSaving(false); }
  };

  const readonlyStyle = { background: "#f8fafc", color: "#64748b" };

  return (
    <div className="pd-overlay">
      <div className="pd-modal" style={{ maxWidth: 540 }}>
        <h3 className="pd-modal-title">{initial?.id ? "Edit Coordinator" : "Add Coordinator"}</h3>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 1rem" }}>

          {/* 1. Type first */}
          <div className="pd-form-group" style={{ gridColumn: "1 / -1" }}>
            <label>Coordinator Type *</label>
            <select value={form.coordinatorType} onChange={(e) => handleTypeChange(e.target.value)}>
              {COORD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {/* 2. Search (STUDENT / SPEAKER) */}
          {isStudent && (
            <div className="pd-form-group" style={{ gridColumn: "1 / -1" }}>
              <label>Select User *</label>
              <UserSearchInput displayName={form.userDisplayName} onChange={handleUserSelect} />
            </div>
          )}
          {form.coordinatorType === "SPEAKER" && (
            <div className="pd-form-group" style={{ gridColumn: "1 / -1" }}>
              <label>Select Speaker *</label>
              <SpeakerSearchInput displayName={form.speakerDisplayName} onChange={handleSpeakerSelect} />
            </div>
          )}

          {/* 3. Contact info — auto-filled for STUDENT, name-only for SPEAKER, all manual for EXTERNAL */}
          <div className="pd-form-group" style={{ gridColumn: "1 / -1" }}>
            <label>Name *</label>
            <input value={form.name} onChange={(e) => set("name", e.target.value)}
              placeholder="Full name" readOnly={isLinked}
              style={isLinked ? readonlyStyle : {}} />
          </div>

          {/* Email — readonly for STUDENT, editable for SPEAKER & EXTERNAL */}
          <div className="pd-form-group" style={{ gridColumn: "1 / -1" }}>
            <label>Email</label>
            <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
              placeholder="email@example.com" readOnly={isStudent}
              style={isStudent ? readonlyStyle : {}} />
          </div>

          {/* Phone — readonly for STUDENT, editable for SPEAKER & EXTERNAL */}
          <div className="pd-form-group" style={{ gridColumn: "1 / -1" }}>
            <label>Phone</label>
            {isStudent ? (
              <input value={form.phone ? `${form.countryCode} ${form.phone}` : ""}
                readOnly style={readonlyStyle} placeholder="—" />
            ) : (
              <PhoneInput
                countryCodes={countryCodes}
                dialCode={form.countryCode}
                onDialCodeChange={(val) => set("countryCode", val)}
                mobileNumber={form.phone}
                onMobileNumberChange={(val) => set("phone", val)}
                placeholder="Mobile Number (Optional)"
              />
            )}
          </div>

          {/* Payment info */}
          <div className="pd-form-group" style={{ gridColumn: "1 / -1" }}>
            <label>Payment Mode</label>
            <select value={form.paymentMode} onChange={(e) => set("paymentMode", e.target.value)}>
              <option value="">— Select —</option>
              {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>

          {form.paymentMode === "BANK" && (<>
            <div className="pd-form-group">
              <label>Bank Name</label>
              <input value={form.bankName} onChange={(e) => set("bankName", e.target.value)} placeholder="e.g. HDFC Bank" />
            </div>
            <div className="pd-form-group">
              <label>Account Holder Name</label>
              <input value={form.accountHolderName} onChange={(e) => set("accountHolderName", e.target.value)} />
            </div>
            <div className="pd-form-group">
              <label>Account Number</label>
              <input value={form.accountNumber} onChange={(e) => set("accountNumber", e.target.value)} />
            </div>
            <div className="pd-form-group">
              <label>IFSC Code</label>
              <input value={form.ifscCode} onChange={(e) => set("ifscCode", e.target.value)} placeholder="e.g. HDFC0001234" />
            </div>
          </>)}

          {form.paymentMode === "UPI" && (
            <div className="pd-form-group" style={{ gridColumn: "1 / -1" }}>
              <label>UPI ID</label>
              <input value={form.upiId} onChange={(e) => set("upiId", e.target.value)} placeholder="name@upi" />
            </div>
          )}

          {(form.paymentMode === "CASH" || form.paymentMode === "OTHER") && (
            <div className="pd-form-group" style={{ gridColumn: "1 / -1" }}>
              <label>Payment Notes</label>
              <input value={form.paymentNotes} onChange={(e) => set("paymentNotes", e.target.value)} placeholder="e.g. Pay in person" />
            </div>
          )}

          {/* Default Commission % */}
          <div className="pd-form-group" style={{ gridColumn: "1 / -1" }}>
            <label>Default Commission % *</label>
            <input type="number" min="0" max="100" step="0.01"
              value={form.defaultCommissionPercent}
              onChange={(e) => set("defaultCommissionPercent", e.target.value)}
              placeholder="e.g. 10.00" />
          </div>

          <div className="pd-form-group" style={{ gridColumn: "1 / -1" }}>
            <label>Notes</label>
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2}
              style={{ padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: "0.875rem", resize: "vertical", outline: "none", fontFamily: "inherit" }}
              placeholder="General notes about this coordinator" />
          </div>
        </div>

        {err && <div className="pd-error">{err}</div>}
        <div className="pd-modal-actions">
          <button className="pd-btn pd-btn-primary" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save"}</button>
          <button className="pd-btn pd-btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ── Add Student Manually Modal ───────────────────────────────
const AddStudentModal = ({ coordinator, onClose, onSuccess, onError }) => {
  const [email,   setEmail]   = useState("");
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState("");

  const submit = async () => {
    if (!email.trim()) { setErr("Email is required."); return; }
    setSaving(true); setErr("");
    try {
      const res = await linkStudentEmail(coordinator.id, { studentEmail: email.trim() });
      onSuccess(typeof res.data === "string" ? res.data : "Student email linked.");
    } catch (e) {
      onError(e?.response?.data?.message || "Failed to link student.");
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <div className="pd-overlay">
      <div className="pd-modal pd-modal-sm">
        <h3 className="pd-modal-title">Add Student Manually — {coordinator.name}</h3>
        <div className="pd-form-group">
          <label>Student Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="student@example.com" />
        </div>
        {err && <div className="pd-error">{err}</div>}
        <div className="pd-modal-actions">
          <button className="pd-btn pd-btn-primary" disabled={saving} onClick={submit}>
            {saving ? "Linking…" : "Link Student"}
          </button>
          <button className="pd-btn pd-btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────
const CoordinatorDashboard = () => {
  const [rows,          setRows]          = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState("");
  const [toast,         setToast]         = useState(null);
  const [modal,         setModal]         = useState(null);
  const [linkModal,     setLinkModal]     = useState(null);
  const [studentModal,  setStudentModal]  = useState(null);
  const [confirm,       setConfirm]       = useState(null);
  const [typeFilter,    setTypeFilter]    = useState("");
  const [activeOnly,    setActiveOnly]    = useState(false);
  const navigate = useNavigate();

  const showToast = (msg, type = "success") => setToast({ msg, type });

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = {};
      if (typeFilter) params.type   = typeFilter;
      if (activeOnly) params.active = true;
      const res = await fetchCoordinators(params);
      setRows(res.data || []);
    } catch { setError("Failed to load coordinators."); }
    finally  { setLoading(false); }
  }, [typeFilter, activeOnly]);

  useEffect(() => { load(); }, [load]);

  const afterSave = (msg) => { setModal(null); setLinkModal(null); showToast(msg); load(); };

  const handleToggle = (r) => {
    const isActive = r.isActive;
    setConfirm({
      message: `${isActive ? "Disable" : "Enable"} coordinator "${r.name}"?`,
      danger: isActive,
      action: async () => {
        try {
          if (isActive) await disableCoordinator(r.id);
          else          await enableCoordinator(r.id);
          setConfirm(null);
          showToast(`Coordinator ${isActive ? "disabled" : "enabled"}.`);
          load();
        } catch { showToast("Action failed.", "error"); setConfirm(null); }
      },
    });
  };

  const handleDelete = (r) => {
    setConfirm({
      message: `Permanently delete coordinator "${r.name}"? This cannot be undone.`,
      danger: true,
      action: async () => {
        try {
          await deleteCoordinator(r.id);
          setConfirm(null);
          showToast("Coordinator deleted.");
          load();
        } catch { showToast("Failed to delete.", "error"); setConfirm(null); }
      },
    });
  };

  return (
    <div className="pd-page">
      {toast     && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
      {confirm   && <ConfirmDialog message={confirm.message} danger={confirm.danger} onConfirm={confirm.action} onCancel={() => setConfirm(null)} />}
      {modal        && <CoordinatorModal initial={modal === "add" ? null : modal} onClose={() => setModal(null)} onSuccess={afterSave} />}
      {linkModal    && <LinkUserModal coordinator={linkModal} onClose={() => setLinkModal(null)} onSuccess={afterSave} />}
      {studentModal && <AddStudentModal coordinator={studentModal} onClose={() => setStudentModal(null)} onSuccess={(msg) => { setStudentModal(null); showToast(msg); }} onError={(msg) => showToast(msg, "error")} />}

      <div className="pd-header">
        <h2 className="pd-title">Coordinators</h2>
        <button className="pd-btn pd-btn-primary" onClick={() => setModal("add")}>+ Add Coordinator</button>
      </div>

      <div className="pd-filter-bar">
        <select className="pd-select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="">All Types</option>
          {COORD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.82rem", color: "#374151", cursor: "pointer" }}>
          <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
          Active only
        </label>
        <button className="pd-btn pd-btn-ghost pd-btn-sm" onClick={() => { setTypeFilter(""); setActiveOnly(false); }}>Clear</button>
      </div>

      {error && <div className="pd-error-bar">{error}</div>}

      <div className="pd-table-area">
        {loading ? (
          <div className="pd-loading">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="pd-empty">
            <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>🤝</div>
            <div>No coordinators found. Add your first coordinator!</div>
          </div>
        ) : (
          <>
            <div className="pd-count">{rows.length} coordinator{rows.length !== 1 ? "s" : ""}</div>
            <div className="pd-table-wrap">
              <table className="pd-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>Payment Mode</th>
                    <th>Commission %</th>
                    <th>Linked</th>
                    <th>Active</th>
                    <th>Since</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td className="pd-cell-bold">{r.name}</td>
                      <td><span className={`pd-badge ${TYPE_BADGE[r.coordinatorType] || "pd-badge-gray"}`}>{r.coordinatorType}</span></td>
                      <td className="pd-cell-sub">{r.countryCode && r.phone ? `${r.countryCode} ${r.phone}` : r.phone || "—"}</td>
                      <td className="pd-cell-sub">{r.email || "—"}</td>
                      <td className="pd-cell-sub">{r.paymentMode || "—"}</td>
                      <td className="pd-cell-sub">
                        {r.defaultCommissionPercent != null ? `${r.defaultCommissionPercent}%` : "—"}
                      </td>
                      <td className="pd-cell-sub">
                        {r.userId    ? <span className="pd-badge pd-badge-blue">User #{r.userId}</span>    : null}
                        {r.speakerId ? <span className="pd-badge pd-badge-purple">Speaker #{r.speakerId}</span> : null}
                        {!r.userId && !r.speakerId ? "—" : null}
                      </td>
                      <td>
                        <span className={`pd-badge ${r.isActive ? "pd-badge-green" : "pd-badge-gray"}`}>
                          {r.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="pd-cell-sub">{fmtDate(r.createdAt)}</td>
                      <td>
                        <div className="pd-cell-actions">
                          <button className="pd-btn pd-btn-ghost pd-btn-sm" onClick={() => setModal(r)}>✏️</button>
                          <button className="pd-btn pd-btn-ghost pd-btn-sm" title="Link User" onClick={() => setLinkModal(r)}>🔗</button>
                          <button className="pd-btn pd-btn-ghost pd-btn-sm" title="Add Student" onClick={() => setStudentModal(r)}>➕</button>
                          <button className="pd-btn pd-btn-ghost pd-btn-sm" title="View Report" onClick={() => navigate(`/admin/coordinators/${r.id}/report`)}>📊</button>
                          <button
                            className={`pd-btn pd-btn-sm ${r.isActive ? "pd-btn-danger-ghost" : "pd-btn-ghost"}`}
                            title={r.isActive ? "Disable" : "Enable"}
                            onClick={() => handleToggle(r)}>
                            {r.isActive ? "⏸" : "▶"}
                          </button>
                          <button className="pd-btn pd-btn-danger-ghost pd-btn-sm" title="Delete" onClick={() => handleDelete(r)}>🗑️</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CoordinatorDashboard;
